// StudyAnalytics
//
// Per-study DICOM breakdown page. Reads the V2.1 'study-details'
// (rpt_study) cache and renders three horizontal Top-N bar charts:
//
//   1. Modalities — split on commas so a multi-modality study like
//      "CT, CR" contributes +1 to both CT and CR (de-duped per study,
//      so "CT, CT" doesn't double-count).
//   2. Study descriptions — verbatim, trimmed.
//   3. Uploading facilities — grouped by facility_id, resolved to the
//      Salesforce facility name via the cached facility list. Each
//      study counts once toward exactly one facility (no fan-out).
//
// The legacy 'studies' cache (sourced from rpt_study_summarized_usage)
// is a daily-per-facility aggregate and CANNOT be used here — it only
// carries study_sent / study_received counters and rolls modality and
// description away. study-details (sourced from rpt_study) is the only
// data type that carries those fields per row.
//
// Scope: honors the global ScopeContext (customer / filter / facility)
// by resolving it to a set of VE facility IDs and intersecting the
// cached records' facility_id. No scope = all facilities.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useCache } from '../hooks/useCache';
import { useScope } from '../contexts/ScopeContext';
import ScopeBar from '../components/ScopeBar';
import client from '../api/client';
import { bucketStudyDescriptions } from '../utils/studyDescriptionNormalize';
import './StudyAnalytics.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const TOP_N = 10;
// Facilities get a wider window because we typically have many more
// distinct uploading sites than modality codes or study descriptions.
// Anything past 20 is rolled into a single "All Others" bar with a
// hover tooltip listing the long tail.
const TOP_N_FACILITIES = 20;
// How many of the long-tail facilities to enumerate in the
// "All Others" tooltip. Anything past this is summarized as
// "+ N more facilities (M studies)" so the tooltip doesn't grow into a
// 100-line scroll wall. 25 is a sweet spot — comfortably visible without
// overrunning a typical viewport.
const OTHERS_DETAIL_CAP = 25;
const OTHERS_LABEL = 'All Others';

// Match the canonical-id helper used in DrillDownModal so facility_id
// lookups behave identically regardless of whether the API returned
// "1706", "1706.0", or 1706.
const canonicalId = (raw) => {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw));
  return Number.isNaN(n) ? null : Math.round(n).toString();
};

// Split a comma-separated modalities string into a Set of normalized
// uppercase tokens. Set ensures "CT, CT" counts CT once for that study.
const tokenizeModalities = (raw) => {
  if (!raw) return new Set();
  return new Set(
    String(raw)
      .split(',')
      .map(t => t.trim().toUpperCase())
      .filter(Boolean)
  );
};

// Shared "Top-N + All Others rollup" reducer used by all three bar
// charts. Sorts entries by count desc, takes the top N as their own
// bars, and bundles the long tail into a single "All Others" bar with
// an attached `othersDetail` payload that the tooltip uses to enumerate
// what's hiding inside.
//
//   entries          : Array of [id, count]
//   topN             : how many real bars to show
//   detailCap        : tooltip enumerates this many tail items, then
//                      summarizes the rest as "+ N more (M studies)"
//   getLabel(id)     : pretty label for the chart axis (e.g. resolve
//                      facility id -> name)
//   othersUnitLabel  : noun for the "(N <unit>)" suffix on the rollup
//                      bar's label, e.g. "facilities" / "modalities"
//
// Returns { data: [[label, count], ...], othersDetail | null }.
function buildTopNWithOthers({ entries, topN, detailCap, getLabel, othersUnitLabel }) {
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  const head = sorted.slice(0, topN);
  const tail = sorted.slice(topN);

  const data = head.map(([id, count]) => [getLabel(id), count]);

  if (tail.length === 0) {
    return { data, othersDetail: null };
  }

  const tailTotal = tail.reduce((acc, [, c]) => acc + c, 0);
  data.push([`${OTHERS_LABEL} (${tail.length} ${othersUnitLabel})`, tailTotal]);

  const enumerated = tail.slice(0, detailCap).map(([id, count]) => ({
    name: getLabel(id),
    count,
  }));
  const overflowItems = tail.length - enumerated.length;
  const overflowTotal = overflowItems > 0
    ? tail.slice(detailCap).reduce((acc, [, c]) => acc + c, 0)
    : 0;

  return {
    data,
    othersDetail: {
      enumerated,
      overflowItems,
      overflowTotal,
      tailTotal,
      tailItems: tail.length,
    },
  };
}

function StudyAnalytics() {
  const cache = useCache();
  const { scope, setScope, clearScope } = useScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateRange, setDateRange] = useState('last90days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);

  // Compact filter strip vs full-form view, mirroring the other analysis
  // pages. DICOM Studies has no Load button (it auto-fetches on date
  // change) so we default to collapsed and only expand when the user
  // taps "Edit filters".
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  // null => no scope filter (show everything). A Set of canonical facility
  // ids => filter records to those facilities only. Computed from the
  // global ScopeContext on every scope change.
  const [scopeFacilityIds, setScopeFacilityIds] = useState(null);

  // Map<canonicalFacilityId, salesforceName>. Populated once on mount
  // from the cached Salesforce facility list so the third bar chart can
  // show readable names instead of bare numeric VE ids. Falls back to
  // "Facility N" when an id is missing from the lookup (e.g. a brand-new
  // facility not yet in Salesforce).
  const [facilityNameById, setFacilityNameById] = useState(new Map());

  // Monotonic request token. Only the result of the LATEST loadData() call
  // is allowed to mutate `records`; anything older is dropped on the floor.
  // This is the fix for the "bars update but stats don't" symptom: when the
  // user toggled the date dropdown twice in quick succession, two fetches
  // were in flight and whichever resolved second was clobbering the
  // newer-but-faster response. The chart re-rendered (because
  // `topModalities` and the stats both come from the same `records`), but
  // the user perceived the disagreement because the bar chart's animation
  // happened to start from the just-overwritten value.
  const loadSeqRef = useRef(0);

  // Honor the deep-link URL params from AnalysisShortcutBar
  // (?customerId=, ?filterId=, ?facilityId=). Promote them into the global
  // ScopeContext on mount, then strip them so subsequent reloads use
  // sessionStorage as the single source of truth (matches the pattern in
  // useScopeSync).
  //
  // The label set here is a TEMPORARY placeholder ("Customer #15"). The
  // upgrade effect below replaces it with the real human name once the
  // customer / filter list loads — same two-pass pattern used by
  // useScopeSync on the other analysis pages.
  useEffect(() => {
    const customerIdParam = searchParams.get('customerId');
    const filterIdParam = searchParams.get('filterId');
    const facilityIdParam = searchParams.get('facilityId');
    if (!customerIdParam && !filterIdParam && !facilityIdParam) return;

    if (customerIdParam) {
      setScope({ kind: 'customer', id: customerIdParam, label: `Customer #${customerIdParam}` });
    } else if (filterIdParam) {
      setScope({ kind: 'filter', id: filterIdParam, label: `Filter #${filterIdParam}` });
    } else if (facilityIdParam) {
      setScope({ kind: 'facility', id: facilityIdParam, label: `Facility ${facilityIdParam}` });
    }

    const next = new URLSearchParams(searchParams);
    next.delete('customerId');
    next.delete('filterId');
    next.delete('facilityId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Upgrade placeholder scope labels ("Customer #15" → "UAB Medicine",
  // "Filter #3" → "Cardiology — Texas", "Facility 1706" → the cached
  // facility name) once the relevant lookup data has loaded.
  //
  // We only fetch the lookup lists lazily — when (a) we have a scope and
  // (b) its label still looks like a placeholder. That way visitors who
  // arrive without a scope don't pay the round-trip.
  useEffect(() => {
    if (!scope?.label) return;

    let cancelled = false;
    (async () => {
      try {
        if (scope.kind === 'customer' && scope.label.startsWith('Customer #')) {
          const res = await client.get('/api/customers');
          if (cancelled) return;
          const c = (res.data?.customers || res.data || []).find(
            x => String(x.id) === String(scope.id)
          );
          if (c?.name) setScope({ ...scope, label: c.name });
        } else if (scope.kind === 'filter' && scope.label.startsWith('Filter #')) {
          const res = await cache.getSavedFilters();
          if (cancelled) return;
          const sf = (res?.filters || []).find(x => String(x.id) === String(scope.id));
          if (sf?.name) setScope({ ...scope, label: sf.name });
        } else if (scope.kind === 'facility' && scope.label.startsWith('Facility ')) {
          const res = await cache.getCachedFacilities();
          if (cancelled) return;
          const target = canonicalId(scope.id);
          const f = (res?.facilities || []).find(
            x => canonicalId(x.vitalEngine_Facility_ID__c) === target
          );
          if (f?.name) setScope({ ...scope, label: f.name });
        }
      } catch (err) {
        // Best-effort upgrade — if the lookup fails the user just sees
        // the placeholder, which still functions correctly.
        console.error('Scope label upgrade failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, setScope, cache]);

  // Build the facility id -> name lookup once. The cached facility list
  // is small (a few hundred rows) and changes rarely, so a single
  // round-trip on mount is fine — no need for live invalidation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await cache.getCachedFacilities();
        if (cancelled) return;
        const map = new Map();
        for (const f of res?.facilities || []) {
          const id = canonicalId(f.vitalEngine_Facility_ID__c);
          if (id && f.name) map.set(id, f.name);
        }
        setFacilityNameById(map);
      } catch (err) {
        console.error('Facility name lookup failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [cache]);

  useEffect(() => {
    let cancelled = false;
    if (!scope) {
      setScopeFacilityIds(null);
      return;
    }
    (async () => {
      try {
        let ids = [];
        if (scope.kind === 'customer') {
          const res = await client.get(`/api/customers/${scope.id}/resolved-facilities`);
          ids = res.data?.facilityIds || [];
        } else if (scope.kind === 'filter') {
          const res = await cache.getSavedFilters();
          const sf = (res?.filters || []).find(f => String(f.id) === String(scope.id));
          ids = sf?.facilityIds || [];
        } else if (scope.kind === 'facility') {
          ids = [String(scope.id)];
        }
        if (cancelled) return;
        setScopeFacilityIds(new Set(ids.map(canonicalId).filter(Boolean)));
      } catch (err) {
        if (cancelled) return;
        console.error('Scope resolve failed:', err);
        setScopeFacilityIds(null);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, cache]);

  // Date range -> [startDate, endDate] (yyyy-MM-dd). Mirrors the calc in
  // CustomerData so the two pages feel identical when picking ranges.
  const dateRangeBounds = useMemo(() => {
    const end = new Date();
    let start = new Date();
    switch (dateRange) {
      case 'last7days':   start.setDate(end.getDate() - 7); break;
      case 'last30days':  start.setDate(end.getDate() - 30); break;
      case 'last90days':  start.setDate(end.getDate() - 90); break;
      case 'last6months': start.setDate(end.getDate() - 180); break;
      case 'last365days': start.setFullYear(end.getFullYear() - 1); break;
      case 'custom':      return { start: startDate, end: endDate };
      default:            start.setDate(end.getDate() - 90);
    }
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  }, [dateRange, startDate, endDate]);

  const loadData = useCallback(async () => {
    if (!dateRangeBounds.start || !dateRangeBounds.end) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await cache.getCachedData('study-details', dateRangeBounds.start, dateRangeBounds.end);
      // Drop the result if a newer request has been kicked off since we
      // started — otherwise an older/slower fetch can overwrite the
      // newer one and the UI ends up in a half-stale state.
      if (seq !== loadSeqRef.current) return;
      setRecords(res?.records || []);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Load study-details failed:', err);
      setError(err?.response?.data?.error || 'Failed to load DICOM study data.');
      setRecords([]);
    } finally {
      // Only the latest call clears the loading flag; otherwise an
      // older fetch settling could prematurely flip loading=false while
      // the newer one is still in flight.
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [cache, dateRangeBounds.start, dateRangeBounds.end]);

  // Auto-load on date-range change. Custom range loads explicitly via
  // the Load button so the user can pick both ends before firing.
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeBounds.start, dateRangeBounds.end]);

  // Apply scope filter in-memory. Faster than re-fetching on scope change
  // and lets the user toggle scope on/off without an API round-trip.
  const filteredRecords = useMemo(() => {
    if (!scopeFacilityIds || scopeFacilityIds.size === 0) return records;
    return records.filter(r => {
      const facId = canonicalId((r.data || r.Data || {}).facility_id);
      return facId && scopeFacilityIds.has(facId);
    });
  }, [records, scopeFacilityIds]);

  // Aggregate every modality occurrence across all records, plus a
  // running total. The total is reported alongside the chart so it is
  // visually obvious why the bars can sum to MORE than `totalStudies`
  // (multi-modality studies contribute +1 to each modality they list).
  const modalityAgg = useMemo(() => {
    const counts = new Map();
    let totalOccurrences = 0;
    let studiesWithAnyModality = 0;
    for (const r of filteredRecords) {
      const tokens = tokenizeModalities((r.data || r.Data || {}).modalities);
      if (tokens.size > 0) studiesWithAnyModality++;
      totalOccurrences += tokens.size;
      for (const tok of tokens) counts.set(tok, (counts.get(tok) || 0) + 1);
    }
    return { counts, totalOccurrences, studiesWithAnyModality };
  }, [filteredRecords]);

  const modalityChart = useMemo(
    () => buildTopNWithOthers({
      entries: Array.from(modalityAgg.counts.entries()),
      topN: TOP_N,
      detailCap: OTHERS_DETAIL_CAP,
      // Modality codes are already short and unambiguous (CT, MR, US…)
      // so the id IS the label.
      getLabel: (id) => id,
      othersUnitLabel: 'modalities',
    }),
    [modalityAgg]
  );
  const topModalities = modalityChart.data;

  // For descriptions, each study has at most ONE description, so bars
  // sum to "studies that have ANY description" (which can be less than
  // total studies if some records have an empty description).
  //
  // Free-text descriptions like "ECHO", "Echocardiogram Complete", and
  // "ECHOCARDIOGRAM COMPLETE 93306" all describe the same procedure but
  // are entered differently per site. We push every raw description
  // through `bucketStudyDescriptions` which normalizes (lowercase,
  // strip CPT codes, drop fillers, run synonyms, sort tokens) and then
  // does a Jaccard merge pass for off-by-one cases. The bucket's
  // display label is the most common ORIGINAL spelling so the chart
  // axis still reads naturally.
  const descriptionAgg = useMemo(() => {
    const rawDescriptions = [];
    let studiesWithDescription = 0;
    let originalDistinct = new Set();
    for (const r of filteredRecords) {
      const raw = (r.data || r.Data || {}).study_description;
      if (!raw) continue;
      const desc = String(raw).trim();
      if (!desc) continue;
      studiesWithDescription++;
      originalDistinct.add(desc);
      rawDescriptions.push(desc);
    }
    const buckets = bucketStudyDescriptions(rawDescriptions);
    // Build (canonicalKey, count) entries for the top-N reducer, plus a
    // sidecar map so the bar label can be the most common original
    // spelling (rather than our internal canonical key).
    const counts = new Map();
    const labels = new Map();
    for (const [key, b] of buckets) {
      counts.set(key, b.count);
      labels.set(key, b.label);
    }
    return {
      counts,
      labels,
      studiesWithDescription,
      originalDistinct: originalDistinct.size,
      bucketedDistinct: buckets.size,
    };
  }, [filteredRecords]);

  const descriptionChart = useMemo(
    () => buildTopNWithOthers({
      entries: Array.from(descriptionAgg.counts.entries()),
      topN: TOP_N,
      detailCap: OTHERS_DETAIL_CAP,
      getLabel: (key) => descriptionAgg.labels.get(key) || key,
      othersUnitLabel: 'descriptions',
    }),
    [descriptionAgg]
  );
  const topDescriptions = descriptionChart.data;

  const totalStudies = filteredRecords.length;

  // Distinct modalities across the (filtered) window. Useful as a glance
  // KPI when the top-N is dominated by a few common ones.
  const totalDistinctModalities = useMemo(() => {
    const set = new Set();
    for (const r of filteredRecords) {
      tokenizeModalities((r.data || r.Data || {}).modalities).forEach(t => set.add(t));
    }
    return set.size;
  }, [filteredRecords]);

  // Distinct descriptions reads from the bucketed map so the KPI
  // matches what the chart actually displays. The raw distinct count
  // (originalDistinct) is kept on descriptionAgg so we can surface
  // "1,503 raw → 812 grouped" in the chart subtitle.
  const totalDistinctDescriptions = descriptionAgg.bucketedDistinct;

  // Aggregate by uploading facility. rpt_study only carries one facility
  // per row (the receiving / uploading facility — the sender lives in
  // rpt_study_images, which we don't cache), so each study contributes
  // exactly +1 to one bar — no fan-out math like modalities. studies
  // missing facility_id are tracked separately so the chart can
  // explicitly call out the orphan count instead of silently dropping
  // them.
  const facilityAgg = useMemo(() => {
    const counts = new Map();
    let studiesWithFacility = 0;
    let studiesMissingFacility = 0;
    for (const r of filteredRecords) {
      const facId = canonicalId((r.data || r.Data || {}).facility_id);
      if (!facId) {
        studiesMissingFacility++;
        continue;
      }
      studiesWithFacility++;
      counts.set(facId, (counts.get(facId) || 0) + 1);
    }
    return {
      counts,
      studiesWithFacility,
      studiesMissingFacility,
      distinctFacilities: counts.size,
    };
  }, [filteredRecords]);

  // Top-N facilities + an optional "All Others" rollup for the long
  // tail. Bar order: largest individual facility → smallest individual
  // facility → "All Others" (always last so the visual reads "these are
  // the leaders, then everyone else combined").
  const facilityChart = useMemo(
    () => buildTopNWithOthers({
      entries: Array.from(facilityAgg.counts.entries()),
      topN: TOP_N_FACILITIES,
      detailCap: OTHERS_DETAIL_CAP,
      // Resolve VE id → Salesforce facility name; fall back to
      // "Facility N" so unresolved sites stay distinguishable.
      getLabel: (id) => facilityNameById.get(id) || `Facility ${id}`,
      othersUnitLabel: 'facilities',
    }),
    [facilityAgg, facilityNameById]
  );

  const topFacilities = facilityChart.data;

  // The actual min/max timestamp present in the (unfiltered) records the
  // backend returned for the selected window. We compute against `records`
  // rather than `filteredRecords` because scope filtering doesn't shrink
  // the cache window — only the requested date range does. This lets us
  // call out the common "Last 6 months but cache only has 30 days"
  // confusion explicitly instead of leaving the user to wonder why two
  // different dropdown options give identical totals.
  const cacheWindow = useMemo(() => {
    if (!records.length) return null;
    let min = null, max = null;
    for (const r of records) {
      const t = r.timestamp || (r.data || r.Data || {}).created_date || (r.data || r.Data || {}).created_at;
      if (!t) continue;
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) continue;
      if (!min || d < min) min = d;
      if (!max || d > max) max = d;
    }
    if (!min || !max) return null;
    const fmt = (d) => d.toISOString().slice(0, 10);
    const dayCount = Math.round((max - min) / 86_400_000) + 1;
    return { start: fmt(min), end: fmt(max), days: dayCount };
  }, [records]);

  // True when the user picked a window strictly larger than the cache
  // actually covers — drives the "you've already pulled everything"
  // hint so identical totals across longer dropdown choices stop being
  // a head-scratcher.
  const windowExceedsCache = useMemo(() => {
    if (!cacheWindow || !dateRangeBounds.start) return false;
    return dateRangeBounds.start < cacheWindow.start;
  }, [cacheWindow, dateRangeBounds.start]);

  const scopeLabel = scope?.label || null;
  const scopeKindLabel = scope?.kind ? scope.kind.charAt(0).toUpperCase() + scope.kind.slice(1) : null;

  // DICOM Studies has no in-page customer / saved-filter dropdowns -
  // its scope is set entirely from the global ScopeContext (via the
  // StickyScopeBar on other pages, or arrival from a customer card).
  // Build the same shape that ScopeBar consumes so the page header
  // matches the rest of the analysis pages.
  const currentScope = scope ? {
    kind: scope.kind,
    name: scopeLabel || `${scopeKindLabel || 'Scope'} #${scope.id}`,
    viewingLabel: scope.kind === 'customer' ? 'Viewing:' :
                  scope.kind === 'filter'   ? 'Viewing saved filter:' :
                                              'Viewing facility:',
    facilityCount: facilityAgg?.distinctFacilities || 0,
    bannerClass: scope.kind === 'customer' ? 'scope-bar' : 'scope-bar scope-bar-filter',
    clear: () => clearScope(),
    clearLabel: scope.kind === 'customer' ? 'Clear customer'
              : scope.kind === 'filter'   ? 'Clear filter'
              :                             'Clear facility',
    clearTitle: null,
  } : {
    kind: 'none',
    name: 'All VitalEngine Facilities',
    viewingLabel: null,
    facilityCount: 0,
    bannerClass: null,
    clear: null,
    clearLabel: null,
    clearTitle: null,
  };

  // Human label for the date-range preset shown in the compact strip.
  const dateRangePresetLabels = {
    last7days: 'Last 7 days',
    last30days: 'Last 30 days',
    last90days: 'Last 90 days',
    last6months: 'Last 6 months',
    last365days: 'Last 365 days',
    custom: 'Custom range',
  };
  const dateRangeLabel = (() => {
    const base = dateRangePresetLabels[dateRange] || 'Date range';
    if (dateRangeBounds?.start && dateRangeBounds?.end) {
      return `${base} (${dateRangeBounds.start} - ${dateRangeBounds.end})`;
    }
    return base;
  })();

  return (
    <div className="study-analytics">
      <div className="page-header">
        <h1>
          DICOM Studies
          <span className="page-header-scope">
            <span className="page-header-sep">&mdash;</span>
            <span className="page-header-name">{currentScope.name}</span>
          </span>
        </h1>
        <p className="page-header-sub">
          Per-study modality and description breakdowns from the{' '}
          <code>study-details</code> cache (<code>rpt_study</code>).
        </p>
      </div>

      {filtersCollapsed ? (
        <div className="filter-strip-bar">
          <div className="filter-strip-summary">
            <div className="filter-strip-item">
              <span className="filter-strip-label">Date range</span>
              <span className="filter-strip-value">{dateRangeLabel}</span>
            </div>
          </div>
          <div className="filter-strip-actions">
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-ghost"
              onClick={() => setFiltersCollapsed(false)}
            >
              Edit filters
            </button>
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-primary"
              onClick={loadData}
              disabled={loading || (dateRange === 'custom' && (!startDate || !endDate))}
            >
              {loading ? 'Loading...' : 'Reload'}
            </button>
          </div>
        </div>
      ) : (
        <div className="filter-strip-bar" style={{ alignItems: 'flex-start' }}>
          <div className="filter-strip-summary">
            <div className="filter-strip-item" style={{ minWidth: '14rem' }}>
              <span className="filter-strip-label">Date range</span>
              <select
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
                className="sa-date-range"
                aria-label="Date range"
              >
                <option value="last7days">Last 7 days</option>
                <option value="last30days">Last 30 days</option>
                <option value="last90days">Last 90 days</option>
                <option value="last6months">Last 6 months</option>
                <option value="last365days">Last 365 days</option>
                <option value="custom">Custom range</option>
              </select>
            </div>
            {dateRange === 'custom' && (
              <div className="sa-custom-range">
                <label>
                  <span>From</span>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </label>
                <label>
                  <span>To</span>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </label>
                <button onClick={loadData} disabled={!startDate || !endDate || loading}>
                  {loading ? 'Loading...' : 'Load'}
                </button>
              </div>
            )}
          </div>
          <div className="filter-strip-actions">
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-primary"
              onClick={() => setFiltersCollapsed(true)}
            >
              Done
            </button>
          </div>
        </div>
      )}

      <ScopeBar scope={currentScope} />

      {loading && <div className="sa-loading">Loading DICOM studies…</div>}
      {error && <div className="sa-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="sa-stats">
            <div className="sa-stat">
              <span className="sa-stat-value">{totalStudies.toLocaleString()}</span>
              <span className="sa-stat-label">Total studies</span>
            </div>
            <div className="sa-stat">
              <span className="sa-stat-value">{totalDistinctModalities.toLocaleString()}</span>
              <span className="sa-stat-label">Distinct modalities</span>
            </div>
            <div className="sa-stat">
              <span className="sa-stat-value">{totalDistinctDescriptions.toLocaleString()}</span>
              <span className="sa-stat-label">Distinct descriptions</span>
            </div>
            <div className="sa-stat">
              <span className="sa-stat-value">{facilityAgg.distinctFacilities.toLocaleString()}</span>
              <span className="sa-stat-label">Distinct uploading facilities</span>
            </div>
          </div>

          {cacheWindow && (
            <div className={`sa-cache-window ${windowExceedsCache ? 'sa-cache-window--warning' : ''}`}>
              <span>
                Cached <code>study-details</code> in this window:{' '}
                <strong>{cacheWindow.start} → {cacheWindow.end}</strong>{' '}
                ({cacheWindow.days} day{cacheWindow.days === 1 ? '' : 's'}).
              </span>
              {windowExceedsCache && (
                <span className="sa-cache-window-note">
                  Your selected range starts {dateRangeBounds.start}, but the
                  cache only goes back to {cacheWindow.start}. Longer dropdown
                  options will return the same data until the cache is
                  refreshed for a wider window from{' '}
                  <strong>Admin → Data Sources → Refresh Panel</strong>.
                </span>
              )}
            </div>
          )}

          {totalStudies === 0 ? (
            <div className="sa-empty">
              <strong>No DICOM studies in the cache for this window.</strong>
              <p>
                {scopeLabel
                  ? 'Try widening the date range, clearing the scope, or refreshing the study-details cache from the admin Refresh Panel.'
                  : 'Try widening the date range, or refresh the study-details cache from the admin Refresh Panel.'}
              </p>
            </div>
          ) : (
            <div className="sa-charts">
              <BarChartCard
                title={
                  modalityChart.othersDetail
                    ? `Top ${TOP_N} Modalities + All Others`
                    : `Top ${Math.min(TOP_N, topModalities.length)} Modalities`
                }
                subtitle={
                  <>
                    Studies per DICOM modality across <strong>{totalStudies.toLocaleString()}</strong>{' '}
                    total studies. Multi-modality studies (e.g. <code>CT, SR</code>) count once per
                    modality they contain, so bars can sum to <strong>more</strong> than total
                    studies — here, <strong>{modalityAgg.totalOccurrences.toLocaleString()}</strong>{' '}
                    modality occurrences across {modalityAgg.studiesWithAnyModality.toLocaleString()}{' '}
                    studies (avg{' '}
                    {modalityAgg.studiesWithAnyModality > 0
                      ? (modalityAgg.totalOccurrences / modalityAgg.studiesWithAnyModality).toFixed(2)
                      : '0'}{' '}
                    per study).
                    {modalityChart.othersDetail
                      ? ` The bottom ${modalityChart.othersDetail.tailItems.toLocaleString()} modalities are rolled into the "${OTHERS_LABEL}" bar; hover it for the breakdown.`
                      : ''}
                  </>
                }
                data={topModalities}
                color="#14b8a6"
                totalForPct={totalStudies}
                pctSuffix=" of total studies"
                othersDetail={modalityChart.othersDetail}
              />
              <BarChartCard
                title={
                  descriptionChart.othersDetail
                    ? `Top ${TOP_N} Study Descriptions + All Others`
                    : `Top ${Math.min(TOP_N, topDescriptions.length)} Study Descriptions`
                }
                subtitle={
                  <>
                    Studies grouped by DICOM Study Description. Similar spellings (e.g. {' '}
                    <em>ECHO</em>, <em>Echocardiogram Complete</em>, <em>ECHOCARDIOGRAM COMPLETE 93306</em>)
                    are bucketed together via case/punctuation/CPT-code stripping, a synonym
                    dictionary, and a token-overlap merge — bar label is the most common
                    spelling in each bucket.
                    {' '}Each study has at most one description, so bars sum to{' '}
                    <strong>{descriptionAgg.studiesWithDescription.toLocaleString()}</strong>{' '}
                    {descriptionAgg.studiesWithDescription === totalStudies
                      ? '(every study has a description).'
                      : `out of ${totalStudies.toLocaleString()} total studies — ${(totalStudies - descriptionAgg.studiesWithDescription).toLocaleString()} have no description.`}
                    {descriptionAgg.originalDistinct > descriptionAgg.bucketedDistinct && (
                      <> Grouped {descriptionAgg.originalDistinct.toLocaleString()} raw spellings into{' '}
                      <strong>{descriptionAgg.bucketedDistinct.toLocaleString()}</strong> buckets.</>
                    )}
                    {descriptionChart.othersDetail
                      ? ` The bottom ${descriptionChart.othersDetail.tailItems.toLocaleString()} buckets are rolled into the "${OTHERS_LABEL}" bar; hover it for the breakdown.`
                      : ''}
                  </>
                }
                data={topDescriptions}
                color="#6366f1"
                truncateLabels
                totalForPct={totalStudies}
                pctSuffix=" of total studies"
                othersDetail={descriptionChart.othersDetail}
              />
              <BarChartCard
                title={
                  facilityAgg.distinctFacilities > TOP_N_FACILITIES
                    ? `Top ${TOP_N_FACILITIES} Uploading Facilities + All Others`
                    : `Top ${Math.min(TOP_N_FACILITIES, topFacilities.length)} Uploading Facilities`
                }
                subtitle={
                  <>
                    Studies grouped by uploading facility (<code>facility_id</code> from{' '}
                    <code>rpt_study</code>, resolved to the Salesforce facility name). Each study
                    counts toward exactly one facility — no fan-out — so bars sum to{' '}
                    <strong>{facilityAgg.studiesWithFacility.toLocaleString()}</strong>{' '}
                    {facilityAgg.studiesMissingFacility > 0
                      ? `out of ${totalStudies.toLocaleString()} total studies (${facilityAgg.studiesMissingFacility.toLocaleString()} have no facility_id).`
                      : '(every study has a facility).'}
                    {' '}Across <strong>{facilityAgg.distinctFacilities.toLocaleString()}</strong>{' '}
                    distinct facilities
                    {facilityChart.othersDetail
                      ? ` — the bottom ${facilityChart.othersDetail.tailItems.toLocaleString()} are rolled into the "${OTHERS_LABEL}" bar; hover it for the breakdown.`
                      : '.'}
                  </>
                }
                data={topFacilities}
                color="#f59e0b"
                truncateLabels
                wide
                totalForPct={totalStudies}
                pctSuffix=" of total studies"
                othersDetail={facilityChart.othersDetail}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Single horizontal bar card. The chart canvas height grows with the
// number of bars so labels stay readable instead of being squashed.
//
// `totalForPct` (optional) is the denominator used when the tooltip
// shows "X studies (Y% of total)". For modalities this is total
// studies, NOT the sum of all modality occurrences — the % is meant to
// answer "what fraction of studies contain this modality?", which is
// the more useful question when a single study can show up in multiple
// bars.
//
// `wide` (optional) — when true, the card spans the full grid width on
// the 2-column desktop layout. Useful for facility names, which are
// long enough that a half-width chart truncates aggressively.
//
// `othersDetail` (optional) — when one of the bars in `data` is the
// rolled-up "All Others" bar, pass the per-facility breakdown of that
// rollup here so the tooltip can enumerate what's hiding inside it.
// Detected via the OTHERS_LABEL prefix on the bar's label, so the chart
// doesn't need a separate "this is the others bar" index.
function BarChartCard({ title, subtitle, data, color, truncateLabels, totalForPct, pctSuffix, wide, othersDetail }) {
  const labels = useMemo(
    () => data.map(([label]) => (truncateLabels && label.length > 50 ? label.slice(0, 47) + '…' : label)),
    [data, truncateLabels]
  );
  const fullLabels = useMemo(() => data.map(([label]) => label), [data]);
  const counts = useMemo(() => data.map(([, count]) => count), [data]);

  // Per-bar colors so the "All Others" rollup is visually distinct from
  // the real facility bars. Same hue, dashed border + lighter opacity
  // — reads as "this isn't one site, it's a bucket" without inventing a
  // new color the legend would have to explain.
  const backgroundColors = useMemo(() => {
    if (!othersDetail) return color;
    return fullLabels.map(l =>
      l.startsWith(OTHERS_LABEL) ? `${color}55` : color
    );
  }, [fullLabels, color, othersDetail]);
  const borderColors = useMemo(() => {
    if (!othersDetail) return color;
    return fullLabels.map(l =>
      l.startsWith(OTHERS_LABEL) ? color : color
    );
  }, [fullLabels, color, othersDetail]);

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      label: 'Studies',
      data: counts,
      backgroundColor: backgroundColors,
      borderColor: borderColors,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }],
  }), [labels, counts, backgroundColors, borderColors]);

  const options = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    // Disable the default 1000ms grow-from-zero animation. With the
    // loadSeqRef guard the chart now legitimately re-renders on every
    // date-range / scope change, and watching all 10 bars crawl outward
    // each time felt sluggish. Snap to final length instead — same
    // behavior as the time-series chart on Data Trends.
    animation: false,
    animations: { colors: false, x: false, y: false },
    transitions: { active: { animation: { duration: 0 } } },
    plugins: {
      legend: { display: false },
      tooltip: {
        // Right-align the per-line text so the count column on the
        // "All Others" breakdown stays on the right edge instead of
        // floating after a variable-width facility name. Looks like a
        // mini scoreboard.
        bodyAlign: 'left',
        callbacks: {
          // Tooltip shows the FULL untruncated label so users with cut-off
          // descriptions can still read what's there.
          title: (items) => fullLabels[items[0].dataIndex],
          label: (ctx) => {
            const fullLabel = fullLabels[ctx.dataIndex] || '';
            const isOthers = othersDetail && fullLabel.startsWith(OTHERS_LABEL);
            const n = ctx.parsed.x;

            if (!isOthers) {
              const base = `${n.toLocaleString()} studies`;
              if (!totalForPct || totalForPct <= 0) return base;
              const pct = (n / totalForPct) * 100;
              return `${base} (${pct.toFixed(1)}%${pctSuffix || ''})`;
            }

            // "All Others" bar — return an array of strings so each
            // becomes its own tooltip line. Header summarizes the
            // rollup, then we enumerate the per-item tail, then an
            // overflow footer if we capped the enumeration. Wording
            // is generic ("items") because this card is shared by
            // modalities / descriptions / facilities.
            const lines = [];
            const pct = totalForPct ? (n / totalForPct) * 100 : null;
            lines.push(
              pct != null
                ? `${n.toLocaleString()} studies (${pct.toFixed(1)}%${pctSuffix || ''})`
                : `${n.toLocaleString()} studies`
            );
            lines.push(`${othersDetail.tailItems.toLocaleString()} items, breakdown:`);
            for (const { name, count } of othersDetail.enumerated) {
              lines.push(`  ${name} — ${count.toLocaleString()}`);
            }
            if (othersDetail.overflowItems > 0) {
              lines.push(
                `  + ${othersDetail.overflowItems.toLocaleString()} more ` +
                `(${othersDetail.overflowTotal.toLocaleString()} studies)`
              );
            }
            return lines;
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
      y: { ticks: { autoSkip: false, font: { size: 12 } } },
    },
  }), [fullLabels, totalForPct, pctSuffix, othersDetail]);

  // ~30px per bar + 60px padding keeps labels legible whether we have 3
  // or 10 entries.
  const canvasHeight = Math.max(220, data.length * 32 + 60);

  return (
    <div className={`sa-chart-card ${wide ? 'sa-chart-card--wide' : ''}`}>
      <h3>{title}</h3>
      <p className="sa-chart-subtitle">{subtitle}</p>
      <div className="sa-chart-canvas" style={{ height: canvasHeight }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

export default StudyAnalytics;
